import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { EnvelopeIcon, ArrowPathIcon, PlayIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { listAllEmailDomains, resumeEmailDomain, getEmailStats } from '../../../../services/emailDomainService';
import EmailVolumeChart from './EmailVolumeChart';
import EmailTemplatesTab from './EmailTemplatesTab';

/**
 * Correo de AR (Resend). Panel de Configuración, admin-only por el guard de
 * `SettingsPage`.
 *
 * Vivió en `/email-domains`, en ruta propia, mientras el frente de Kapso
 * reestructuraba `/settings`; se trasladó aquí al integrarse las dos ramas,
 * que era lo acordado el 04/09/2026.
 *
 * **Todas las empresas van en la misma tabla, a propósito.** Una pantalla por
 * empresa obliga a entrar una por una para ver si alguna va mal, que es justo lo
 * que nadie hace. Y por eso lo peor sale arriba: un panel ordenado por
 * antigüedad esconde el problema en la fila catorce.
 *
 * Lo que de verdad importa aquí no son las estadísticas —el volumen de AR es aún
 * pequeño—, sino **poder ver y levantar las pausas**: la etapa 2 corta el envío
 * de un cliente cuando sus rebotes se disparan, y hasta ahora eso no se veía en
 * ninguna parte.
 */

const STATUS_STYLES = {
  verified: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  not_started: 'bg-slate-100 text-slate-600',
  failure: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  verified: 'Verificado',
  pending: 'Propagando',
  not_started: 'Sin empezar',
  failure: 'Fallido',
};

const HEALTH_STYLES = {
  ok: 'bg-green-100 text-green-700',
  warn: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
};

const VENTANAS = [
  [7, '7 días'],
  [30, '30 días'],
  [90, '90 días'],
];

const pct = n => `${(n * 100).toFixed(n >= 0.01 ? 1 : 2)}%`;

const Select = ({ id, label, value, onChange, children }) => (
  <div>
    <label htmlFor={id} className='block text-xs font-medium text-slate-600 mb-1'>
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={e => onChange(e.target.value)}
      className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
    >
      {children}
    </select>
  </div>
);

/** Salud de la company, con el matiz de si se ha llegado a evaluar. */
const HealthBadge = ({ health }) => {
  if (!health) return null;

  // Por debajo del volumen mínimo los ratios no significan nada, y decir «ok»
  // a secas sería afirmar algo que no sabemos.
  if (!health.evaluated) {
    return <span className='text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-600'>Sin datos suficientes</span>;
  }

  return (
    <span
      className={`text-xs px-2 py-1 rounded-lg ${HEALTH_STYLES[health.level] || HEALTH_STYLES.ok}`}
      title={health.reasons?.join(' · ')}
    >
      {health.level === 'ok' ? 'Sana' : health.level === 'warn' ? 'Aviso' : 'Crítica'}
      {' · '}
      {pct(health.bounce_rate)} rebotes
    </span>
  );
};

const EmailSettings = () => {
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cargandoStats, setCargandoStats] = useState(true);
  const [resuming, setResuming] = useState(null);

  const [empresa, setEmpresa] = useState('');
  const [estado, setEstado] = useState('');
  const [orden, setOrden] = useState('atencion');
  const [pestana, setPestana] = useState('dominios');
  const [ventana, setVentana] = useState(30);

  const load = useCallback(async () => {
    try {
      setDomains(await listAllEmailDomains());
    } catch (err) {
      console.error('Error cargando los dominios de correo:', err);
      toast.error(err.response?.data?.error?.message || 'No se pudieron cargar los dominios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // El gráfico se recarga al cambiar de empresa o de ventana; la tabla no, que
  // ya la tenemos entera y se filtra en memoria.
  useEffect(() => {
    let vigente = true;
    setCargandoStats(true);

    getEmailStats({ days: ventana, companyId: empresa })
      .then(data => {
        if (vigente) setStats(data);
      })
      .catch(err => {
        console.error('Error cargando las estadísticas de correo:', err);
        if (vigente) setStats(null);
      })
      .finally(() => {
        if (vigente) setCargandoStats(false);
      });

    return () => {
      vigente = false;
    };
  }, [empresa, ventana]);

  const handleResume = async domain => {
    if (
      !window.confirm(
        `Vas a reanudar el envío de ${domain.display_name}.\n\n` +
          'Hazlo solo si has revisado su lista de destinatarios: si sigue mal, ' +
          'el siguiente chequeo lo volverá a pausar y el dominio se desgasta.'
      )
    ) {
      return;
    }

    setResuming(domain._id);
    try {
      await resumeEmailDomain(domain._id);
      toast.success('Envío reanudado');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'No se pudo reanudar');
    } finally {
      setResuming(null);
    }
  };

  // Una company puede tener varios dominios: nos quedamos con el primer nombre
  // que aparece, que es el que ya se está usando como remitente.
  const empresas = useMemo(() => {
    const m = new Map();
    domains.forEach(d => {
      if (!m.has(d.company_id)) m.set(d.company_id, d.display_name);
    });
    return [...m.entries()];
  }, [domains]);

  const visibles = useMemo(() => {
    let filas = domains.filter(d => {
      if (empresa && d.company_id !== empresa) return false;
      if (estado === 'paused') return d.is_paused;
      if (estado) return d.status === estado;
      return true;
    });

    if (orden === 'empresa') {
      filas = [...filas].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'es'));
    } else if (orden === 'cupo') {
      filas = [...filas].sort((a, b) => (b.capacity?.used || 0) - (a.capacity?.used || 0));
    }
    // `atencion` es el orden que ya trae el backend: pausados y críticos
    // primero. No se recalcula aquí para no tener dos criterios distintos.

    return filas;
  }, [domains, empresa, estado, orden]);

  const pausados = domains.filter(d => d.is_paused).length;
  const sinVerificar = domains.filter(d => d.status !== 'verified').length;

  return (
    <div className='space-y-4'>
      <div className='bg-white rounded-lg border border-slate-200 p-4 flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-semibold text-slate-900 flex items-center gap-2'>
            <EnvelopeIcon className='w-5 h-5 text-blue-600' />
            Email AR · Resend
          </h2>
          <p className='mt-1 text-sm text-slate-500'>
            Dominios de envío de cada cliente y la escalera de plantillas con la que se les
            contacta en frío. El correo sale con la identidad de la empresa, no con la de ClaimUp.
          </p>
        </div>
        <button
          type='button'
          onClick={load}
          className='inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800'
        >
          <ArrowPathIcon className='w-4 h-4' />
          Actualizar
        </button>
      </div>

      <div className='flex gap-1 border-b border-slate-200 mb-4'>
        {[
          ['dominios', 'Dominios'],
          ['plantillas', 'Plantillas'],
        ].map(([valor, etiqueta]) => (
          <button
            key={valor}
            type='button'
            onClick={() => setPestana(valor)}
            aria-current={pestana === valor ? 'page' : undefined}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              pestana === valor
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {pestana === 'plantillas' ? (
        <EmailTemplatesTab empresas={empresas} />
      ) : (
        <>
          {(pausados > 0 || sinVerificar > 0) && (
            <div className='flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4'>
              <ExclamationTriangleIcon className='w-4 h-4 text-amber-600 shrink-0 mt-0.5' />
              <p className='text-sm text-amber-800'>
                {pausados > 0 && (
                  <>
                    <span className='font-medium'>{pausados}</span> dominio{pausados > 1 ? 's' : ''} pausado
                    {pausados > 1 ? 's' : ''} por salud.{' '}
                  </>
                )}
                {sinVerificar > 0 && (
                  <>
                    <span className='font-medium'>{sinVerificar}</span> sin verificar.
                  </>
                )}
              </p>
            </div>
          )}

          <div className='bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-4 mb-4'>
            <Select id='correo-empresa' label='Empresa' value={empresa} onChange={setEmpresa}>
              <option value=''>Todas</option>
              {empresas.map(([id, nombre]) => (
                <option key={id} value={id}>
                  {nombre}
                </option>
              ))}
            </Select>

            <Select id='correo-estado' label='Estado' value={estado} onChange={setEstado}>
              <option value=''>Todos</option>
              {Object.entries(STATUS_LABELS).map(([valor, etiqueta]) => (
                <option key={valor} value={valor}>
                  {etiqueta}
                </option>
              ))}
              <option value='paused'>Pausados</option>
            </Select>

            <Select
              id='correo-ventana'
              label='Ventana del gráfico'
              value={String(ventana)}
              onChange={v => setVentana(Number(v))}
            >
              {VENTANAS.map(([valor, etiqueta]) => (
                <option key={valor} value={valor}>
                  {etiqueta}
                </option>
              ))}
            </Select>

            <Select id='correo-orden' label='Ordenar por' value={orden} onChange={setOrden}>
              <option value='atencion'>Atención</option>
              <option value='empresa'>Empresa</option>
              <option value='cupo'>Enviado hoy</option>
            </Select>
          </div>

          <div className='mb-4'>
            <EmailVolumeChart stats={stats} isLoading={cargandoStats} />
          </div>

          {isLoading ? (
            <div className='flex justify-center py-12'>
              <div className='animate-spin rounded-lg h-8 w-8 border-b-2 border-blue-500'></div>
            </div>
          ) : domains.length === 0 ? (
            <div className='text-center py-12 bg-white border border-slate-200 rounded-lg'>
              <p className='text-sm text-slate-500'>Ninguna empresa ha conectado su correo todavía.</p>
              <p className='text-xs text-slate-400 mt-1'>
                Se conecta desde el perfil de cada empresa, en el bloque «Correo electrónico».
              </p>
            </div>
          ) : visibles.length === 0 ? (
            <div className='bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500'>
              Ningún dominio cumple ese filtro.
            </div>
          ) : (
            <div className='bg-white rounded-lg border border-slate-200 overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 text-left'>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Empresa</th>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Dirección de salida</th>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Estado</th>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Salud</th>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Cupo de hoy</th>
                    <th className='px-4 py-3 text-xs font-medium text-slate-500'>Enviados (7 d)</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(d => (
                    <React.Fragment key={d._id}>
                      <tr className={`border-b border-slate-100 ${d.is_paused ? 'bg-red-50' : 'last:border-b-0'}`}>
                        <td className='px-4 py-3'>
                          <span className='text-slate-900'>{d.display_name}</span>
                          <span className='block text-[11px] text-slate-400'>
                            {d.mode === 'managed' ? 'Gestionado' : 'Suyo'}
                          </span>
                        </td>
                        <td className='px-4 py-3'>
                          <span className='text-xs font-mono text-slate-700'>{d.from_address || d.domain}</span>
                        </td>
                        <td className='px-4 py-3'>
                          <span
                            className={`text-xs px-2 py-1 rounded-lg ${
                              STATUS_STYLES[d.status] || STATUS_STYLES.not_started
                            }`}
                          >
                            {STATUS_LABELS[d.status] || d.status}
                          </span>
                        </td>
                        <td className='px-4 py-3'>
                          <HealthBadge health={d.health} />
                        </td>
                        <td className='px-4 py-3'>
                          <span className='text-slate-800'>
                            {d.capacity.used} / {d.capacity.cap}
                          </span>
                          <span className='block text-[11px] text-slate-400'>
                            {d.capacity.warmed_up
                              ? 'Calentado'
                              : `Día ${d.capacity.warmup_day} · techo ${d.capacity.ceiling}`}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-slate-600'>{d.health?.totals?.sent ?? 0}</td>
                      </tr>

                      {d.is_paused && (
                        <tr className='border-b border-slate-100 bg-red-50'>
                          <td colSpan={6} className='px-4 pb-3'>
                            <div className='flex items-start justify-between gap-3'>
                              <div>
                                <p className='text-sm font-medium text-red-800'>Envío pausado</p>
                                <p className='text-xs text-red-700'>{d.pause_reason}</p>
                              </div>
                              <button
                                type='button'
                                onClick={() => handleResume(d)}
                                disabled={resuming === d._id}
                                className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium ${
                                  resuming === d._id
                                    ? 'bg-blue-300 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                              >
                                <PlayIcon className='w-3.5 h-3.5' />
                                {resuming === d._id ? 'Reanudando…' : 'Reanudar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailSettings;
